import { useRef, useCallback } from 'react';

/**
 * usePendingApprovals — track in-flight per-paragraph approval writes so the
 * "Save & Exit" / "Complete Review" flow can wait for them to land before it
 * computes and persists the section's "fully approved" flag.
 *
 * Why this exists (clinical-safety context):
 * When a clinician taps "Approve" on a paragraph, the Review Document pages
 * update the on-screen progress OPTIMISTICALLY (instantly) but fire the actual
 * Firestore write without awaiting it, so the UI stays snappy. If the clinician
 * then immediately hits "Save & Exit", the exit handler could read the document
 * and compute the approval flag BEFORE those approval writes had committed —
 * persisting "fully approved = false" (or, worse, letting a stale read drive a
 * downstream render) even though the clinician had in fact approved everything.
 * This was a proven cause of Patient Reports finalising with missing content.
 *
 * This tiny tracker records each approval write's promise and lets the exit
 * flow await all of them (via allSettled, so one failed write can't hang the
 * exit) before it reads Firestore and decides the section's approval state.
 *
 * It intentionally does NOT change the optimistic UI: progress still updates
 * instantly; we only add a barrier at the exit boundary.
 *
 * @returns {{ track: (promise: Promise) => Promise, settleAll: () => Promise<void>, pendingCount: () => number }}
 */
export function usePendingApprovals() {
  // A ref (not state) because tracking in-flight writes must not trigger
  // re-renders and must survive across renders without being reset.
  const pendingRef = useRef(new Set());

  // Register an approval write. The promise is removed from the set once it
  // settles (resolve or reject) so the set only ever holds truly in-flight writes.
  const track = useCallback((promise) => {
    const wrapped = Promise.resolve(promise);
    pendingRef.current.add(wrapped);
    // Remove from the set once the write settles. The `.catch` swallows the
    // rejection on THIS cleanup branch only (so a failed approval write does
    // not surface as an unhandled promise rejection); the original `wrapped`
    // promise is returned unchanged so the caller remains responsible for
    // handling its own errors.
    wrapped
      .finally(() => {
        pendingRef.current.delete(wrapped);
      })
      .catch(() => {});
    return wrapped;
  }, []);

  // Wait for every currently-in-flight approval write to settle. allSettled
  // (not all) so a single rejected write does not prevent the exit flow from
  // proceeding — the exit read then reflects whatever actually committed.
  const settleAll = useCallback(async () => {
    await Promise.allSettled([...pendingRef.current]);
  }, []);

  const pendingCount = useCallback(() => pendingRef.current.size, []);

  return { track, settleAll, pendingCount };
}
